import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  assignLead,
  listApplications,
  type NewApplicationListItem,
  type NewApplicationStageFilter,
  type NewApplicationSort,
} from "api/applications";
import { listUsers, type AdminUser } from "api/users";
import { getNewApplicationPathForLead } from "utils/newApplicationPathForLead";

import iconSearch from "../../assets/svg/icon-search.svg";
import iconFilter from "../../assets/svg/icon-filter.svg";
import iconFilterSort from "../../assets/svg/icon-filter-sort.svg";
import iconCheckbox from "../../assets/svg/icon-checkbox.svg";
import iconMore from "../../assets/svg/icon-more.svg";

const PAGE_SIZE = 14;

const STAGE_OPTIONS: { value: NewApplicationStageFilter | "ALL"; label: string }[] = [
  { value: "ALL", label: "All stages" },
  { value: "DISCUSSION", label: "Discussion" },
  { value: "FILING", label: "Filing" },
  { value: "STATUS", label: "Status" },
];

const SORT_OPTIONS: { value: NewApplicationSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

function formatCreatedAt(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

export default function AllNewApplicationsView() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<NewApplicationListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);
  const [reassignLead, setReassignLead] = useState<NewApplicationListItem | null>(null);
  const [internalUsers, setInternalUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [stageFilter, setStageFilter] = useState<NewApplicationStageFilter | "ALL">("ALL");
  const [sortOrder, setSortOrder] = useState<NewApplicationSort>("newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search: update 300ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset to page 1 when search/filter/sort changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stageFilter, sortOrder]);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!filterOpen && !sortOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterOpen && filterRef.current && !filterRef.current.contains(t)) {
        setFilterOpen(false);
      }
      if (sortOpen && sortRef.current && !sortRef.current.contains(t)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [filterOpen, sortOpen]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listApplications({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        stage: stageFilter === "ALL" ? undefined : stageFilter,
        sort: sortOrder,
      });
      setItems(data.items);
      setTotalRecords(data.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load applications");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, stageFilter, sortOrder]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((row) => row.id)));
    }
  };

  const toggleSelectRow = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = async (leadId: string) => {
    setNavigatingId(leadId);
    try {
      const path = await getNewApplicationPathForLead(leadId, { preferLastCompleted: true });
      navigate(path);
    } finally {
      setNavigatingId(null);
    }
  };

  const openReassignModal = async (row: NewApplicationListItem) => {
    setReassignLead(row);
    setAssigneeId("");
    setAssignReason("");
    if (internalUsers.length > 0) return;
    setUsersLoading(true);
    try {
      const users = await listUsers();
      const eligibleUsers = users.filter(
        (user) =>
          user.isActive &&
          (user.role === "SUPER_ADMIN" || user.role === "ADMIN" || user.role === "STAFF")
      );
      setInternalUsers(eligibleUsers);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const closeReassignModal = () => {
    if (assigning) return;
    setReassignLead(null);
  };

  const handleReassignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reassignLead) return;
    if (!assigneeId) {
      toast.error("Please select an employee");
      return;
    }

    setAssigning(true);
    try {
      const message = await assignLead(reassignLead.id, {
        toUserId: assigneeId,
        reason: assignReason.trim() || undefined,
      });
      toast.success(message);
      setReassignLead(null);
      await fetchApplications();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reassign lead");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="New Application">
      <div className="flex-1 p-2 min-h-0 overflow-auto">
        <div className="bg-white rounded-[8px] p-5 flex flex-col gap-6 h-full">
          {/* Title row - space-between */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h1 className="text-[20px] font-semibold leading-5 text-[#222222]">
              All New Applications
            </h1>
            <div className="flex flex-row items-center gap-3 md:gap-4 flex-wrap">
              {/* Search - 250x36, bg #F7F7F7, radius 6 */}
              <div className="flex flex-row items-center gap-2 px-3 py-2 w-full sm:w-[250px] h-9 bg-[#F7F7F7] rounded-[6px]">
                <img src={iconSearch} alt="" className="w-3.5 h-3.5 shrink-0" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 min-w-0 bg-[#F7F7F7] text-xs font-normal leading-5 text-[#777777] placeholder:text-[#777777] focus:outline-none"
                />
              </div>
              {/* Filter — Stage */}
              <div ref={filterRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setFilterOpen((v) => !v);
                    setSortOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 h-9 border rounded-[6px] transition-colors ${
                    stageFilter !== "ALL"
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={filterOpen}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {stageFilter === "ALL"
                      ? "Filter"
                      : `Stage: ${
                          STAGE_OPTIONS.find((o) => o.value === stageFilter)?.label
                        }`}
                  </span>
                </button>
                {filterOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 mt-1 w-44 bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30"
                  >
                    {STAGE_OPTIONS.map((opt) => {
                      const selected = stageFilter === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setStageFilter(opt.value);
                            setFilterOpen(false);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Sort — Created on */}
              <div ref={sortRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setSortOpen((v) => !v);
                    setFilterOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 h-9 border rounded-[6px] transition-colors ${
                    sortOrder !== "newest"
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={sortOpen}
                >
                  <img src={iconFilterSort} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {SORT_OPTIONS.find((o) => o.value === sortOrder)?.label ?? "Sort"}
                  </span>
                </button>
                {sortOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 mt-1 w-44 bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30"
                  >
                    {SORT_OPTIONS.map((opt) => {
                      const selected = sortOrder === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setSortOrder(opt.value);
                            setSortOpen(false);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Create New - 101x36, bg #222, white text */}
              <button
                type="button"
                onClick={() =>
                  navigate("/dashboard/applications/new-application/discussion/preliminary-discussion-with-the-client")
                }
                className="flex items-center justify-center px-4 py-2 h-9 bg-[#222222] rounded-[6px] hover:bg-[#333333] transition-colors"
              >
                <span className="text-xs font-semibold leading-5 text-white">Create New</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="border border-[#EEEEEE] rounded-[8px] flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full min-w-[1100px] border-collapse table-fixed">
                <colgroup>
                  <col className="w-[48px]" />
                  <col className="w-[160px]" />
                  <col className="w-[150px]" />
                  <col className="w-[160px]" />
                  <col className="w-[220px]" />
                  <col className="w-[200px]" />
                  <col className="w-[140px]" />
                  <col className="w-[170px]" />
                  <col className="w-[56px]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#F7F7F7] border-b border-[#EEEEEE]">
                    <th className="sticky top-0 left-0 z-20 bg-[#F7F7F7] px-5 h-11 text-left border-r border-[#EEEEEE]">
                      <button
                        type="button"
                        onClick={toggleSelectAll}
                        disabled={loading || items.length === 0}
                        className="w-6 h-6 flex items-center justify-center disabled:opacity-40"
                        aria-label="Select all"
                      >
                        <img src={iconCheckbox} alt="" className="w-6 h-6" />
                      </button>
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Lead No
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Mark
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      WhatsApp Number
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Email Address
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Client Name
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Stage
                    </th>
                    <th className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]">
                      Created On
                    </th>
                    <th className="sticky top-0 right-0 z-20 bg-[#F7F7F7] px-5 h-11" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-16 text-center text-sm text-[#777777]"
                      >
                        Loading applications…
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-16 text-center text-sm text-[#777777]"
                      >
                        No applications yet. Click &quot;Create New&quot; to get started.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr
                        key={row.id}
                        className={`group hover:bg-[#FAFAFA] transition-colors cursor-pointer ${
                          navigatingId === row.id ? "opacity-60 pointer-events-none" : ""
                        }`}
                        onClick={() => handleRowClick(row.id)}
                      >
                        <td
                          className="sticky left-0 z-[1] bg-white group-hover:bg-[#FAFAFA] px-5 py-4 border-r border-[#F0F0F0] align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={(e) => toggleSelectRow(e, row.id)}
                            className="w-6 h-6 flex items-center justify-center"
                            aria-label={`Select ${row.leadNo}`}
                          >
                            <img src={iconCheckbox} alt="" className="w-6 h-6" />
                          </button>
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-medium leading-5 text-[#222222] tabular-nums truncate">
                          {row.leadNo}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate"
                          title={row.markName ?? undefined}
                        >
                          {row.markName ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] tabular-nums truncate"
                          title={row.phone ?? undefined}
                        >
                          {row.phone ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] truncate"
                          title={row.email ?? undefined}
                        >
                          {row.email ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate"
                          title={row.clientName || undefined}
                        >
                          {row.clientName || <span className="text-[#999999]">–</span>}
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate">
                          {row.stage ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] whitespace-nowrap">
                          {formatCreatedAt(row.createdAt)}
                        </td>
                        <td className="px-5 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => void openReassignModal(row)}
                            className="w-7 h-7 flex items-center justify-center rounded-[6px] hover:bg-[#F0F0F0] transition-colors ml-auto"
                            aria-label="Reassign lead"
                            title="Reassign lead"
                          >
                            <img src={iconMore} alt="" className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-row justify-end items-center gap-6 shrink-0">
            <span className="text-xs font-normal leading-5 text-[#6D6D6D]">
              {totalRecords === 0
                ? "No records"
                : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                    page * PAGE_SIZE,
                    totalRecords
                  )} of ${totalRecords} records`}
            </span>
            <div className="flex flex-row items-center gap-3">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="rotate-180">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {reassignLead && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="w-full max-w-[520px] rounded-[8px] bg-white border border-[#EEEEEE] shadow-lg">
            <div className="px-5 py-4 border-b border-[#EEEEEE]">
              <h2 className="text-[16px] font-semibold text-[#222222]">Reassign Lead</h2>
              <p className="mt-1 text-xs text-[#6D6D6D]">
                Lead: {reassignLead.leadNo} {reassignLead.markName ? `| ${reassignLead.markName}` : ""}
              </p>
            </div>
            <form onSubmit={(e) => void handleReassignSubmit(e)} className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#222222] mb-1">Assign to</label>
                <select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  disabled={usersLoading || assigning}
                  className="w-full h-10 rounded-md border border-[#EEEEEE] px-2.5 text-[12px] text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                >
                  <option value="">
                    {usersLoading ? "Loading users..." : "Select employee"}
                  </option>
                  {internalUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#222222] mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={assignReason}
                  onChange={(e) => setAssignReason(e.target.value)}
                  maxLength={500}
                  disabled={assigning}
                  placeholder="Add a reason for reassignment..."
                  className="w-full min-h-[96px] rounded-md border border-[#EEEEEE] px-2.5 py-2 text-[12px] text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeReassignModal}
                  disabled={assigning}
                  className="h-9 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-xs font-semibold text-[#6D6D6D] hover:bg-[#F7F7F7] disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assigning || usersLoading || !assigneeId}
                  className="h-9 px-4 rounded-[6px] bg-[#222222] text-xs font-semibold text-white hover:bg-[#333333] disabled:opacity-60"
                >
                  {assigning ? "Reassigning..." : "Reassign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
